import React from 'react';

import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import { evalFileName, parseExceptions, qualListCell } from './helpers';
import logo from '../../../assets/logo-black-text.png';
import { formatIsoToDate } from '../../../utils';

// CP-854 — printable evaluation report for one DqfAudit. The audit results live inline on the
// record (exceptions is AWSJSON in DynamoDB, there is no stored artifact), so the document is
// rendered client-side from the same data the pane shows.
//
// Deliberately excludes the AI telemetry on the record (promptVersion, model, effort, token
// counts, processingTimeMs, requestId) — this PDF leaves Centerline.
//
// Brand red is a literal here on purpose: react-pdf paints to a canvas and cannot resolve the
// var(--primary) CSS variable the rest of the app uses. #c8102e is Centerline Red / PMS 186C and
// matches the existing quote PDF (QuoteEngine/GenerateQuote/quoteDocument.js).
const BRAND_RED = '#c8102e';
const LABEL_GREY = '#53565a';
const TEXT_GREY = '#333333';

const styles = StyleSheet.create({
  page: {
    paddingVertical: 24,
    paddingHorizontal: 32,
    fontSize: 11,
    color: TEXT_GREY,
  },
  logo: {
    width: 230,
    height: 60,
  },
  title: {
    fontSize: 18,
    color: BRAND_RED,
    marginTop: 8,
  },
  fileName: {
    fontSize: 12,
    marginTop: 2,
  },
  divider: {
    borderTop: 1,
    borderColor: '#dddddd',
    marginVertical: 10,
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  infoItem: {
    width: '50%',
    marginBottom: 6,
  },
  // The three summary fields fit one row; at 50% the last one would wrap onto a line of its own.
  summaryItem: {
    width: '33.33%',
    marginBottom: 6,
  },
  infoLabel: {
    fontSize: 8,
    color: LABEL_GREY,
  },
  infoValue: {
    fontSize: 11,
  },
  sectionHeading: {
    fontSize: 13,
    color: BRAND_RED,
    marginBottom: 8,
  },
  exception: {
    marginBottom: 12,
  },
  exceptionTitle: {
    fontSize: 12,
    marginBottom: 3,
  },
  exceptionLine: {
    marginBottom: 2,
  },
  label: {
    color: LABEL_GREY,
  },
  message: {
    fontSize: 11,
  },
  error: {
    fontSize: 11,
    color: BRAND_RED,
  },
});

// `??`, not `||`, to match the pane: a Tier of 0 is falsy but is a real value, and `||` blanked it
// in the document while the screen showed it.
const infoRow = (label, value, style = styles.infoItem) => (
  <View style={style} key={label}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoValue}>{value ?? ''}</Text>
  </View>
);

const exceptionLine = (label, value) => (
  <Text style={styles.exceptionLine}>
    <Text style={styles.label}>{label}: </Text>
    {value ?? ''}
  </Text>
);

// Mirrors renderPane() in index.js so the document never disagrees with the screen.
const renderFindings = audit => {
  if (audit?.status === 'ERROR') {
    return (
      <Text style={styles.error}>
        {audit.resultNote || 'System Error, could not evaluate'}
        {audit.errorMessage ? ` — ${audit.errorMessage}` : ''}
      </Text>
    );
  }
  const exceptions = parseExceptions(audit);
  if (!exceptions.length) return <Text style={styles.message}>No Issues found.</Text>;
  return exceptions.map((ex, index) => (
    // wrap={false} keeps a single finding from being split across a page break.
    <View style={styles.exception} wrap={false} key={`${ex.number}-${ex.title}`}>
      <Text style={styles.exceptionTitle}>
        {index + 1}. {ex.title}
      </Text>
      {exceptionLine('Rule', ex.rule)}
      {exceptionLine('Evidence', ex.evidence)}
      {exceptionLine('Severity', ex.severity)}
      {exceptionLine('Page', ex.page)}
    </View>
  ));
};

const EvaluationDocument = ({ audit, header, dqfId }) => {
  const exceptionCount = audit?.status === 'ERROR' ? 0 : parseExceptions(audit).length;
  const info = [
    ['File #', header?.id || dqfId],
    ['Client', header?.name],
    ['Driver', header?.driverName],
    ['Status', header?.status],
    ['DQF Specialist', header?.dqSpecialistUser],
    ['Created', header?.createdAt ? formatIsoToDate(header.createdAt) : ''],
    ['Branch', header?.branch],
    ['Tier', header?.tierDescription || header?.tier],
  ];
  const summary = [
    ['Evaluated', audit?.createdAt ? formatIsoToDate(audit.createdAt) : ''],
    ['Result', audit?.resultNote || 'No Data'],
    ['Qual List Available', qualListCell(audit)],
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image source={logo} style={styles.logo} />
        <Text style={styles.title}>AI Review — Evaluation</Text>
        <Text style={styles.fileName}>{evalFileName(audit)}</Text>

        <View style={styles.divider} />
        <View style={styles.infoGrid}>{info.map(([label, value]) => infoRow(label, value))}</View>

        <View style={styles.divider} />
        <View style={styles.infoGrid}>
          {summary.map(([label, value]) => infoRow(label, value, styles.summaryItem))}
        </View>

        <View style={styles.divider} />
        <Text style={styles.sectionHeading}>
          {audit?.status === 'ERROR' ? 'EVALUATION ERROR' : `EXCEPTIONS (${exceptionCount})`}
        </Text>
        {renderFindings(audit)}
      </Page>
    </Document>
  );
};

export default EvaluationDocument;
